/**
 * 차트 데이터 타입 검증 유틸리티
 */

/**
 * Badge 차트 값 검증 및 정규화
 * @param {*} value - 검증할 값
 * @param {Object} options - 옵션
 * @returns {*} 정규화된 값
 */
export const validateBadgeValue = (value, options = {}) => {
  const { defaultValue = 'N/A', allowNull = false } = options;
  
  // null/undefined 처리
  if (value === null || value === undefined) {
    return allowNull ? null : defaultValue;
  }
  
  // 빈 문자열 처리
  if (value === '') {
    return defaultValue;
  }
  
  // 숫자 타입은 그대로 반환
  if (typeof value === 'number') {
    return value;
  }
  
  // 문자열 타입
  if (typeof value === 'string') {
    // 숫자로 변환 가능한지 확인
    const trimmed = value.trim();
    if (trimmed === '') {
      return defaultValue;
    }
    
    // 숫자 문자열인지 확인 (예: "123", "45.67")
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed);
      if (!isNaN(num)) {
        return num;
      }
    }
    
    // 숫자가 아닌 문자열은 그대로 반환
    return value;
  }
  
  // 불린 타입
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // 기타 타입은 문자열로 변환
  try {
    return String(value);
  } catch (e) {
    return defaultValue;
  }
};

/**
 * 차트 데이터 시리즈 검증
 * @param {Object} chartData - 차트 데이터
 * @param {string} vizType - 차트 타입
 * @returns {boolean} 유효성 여부
 */
export const validateChartSeries = (chartData, vizType) => {
  if (!chartData || !chartData.data) {
    return false;
  }
  
  const { data } = chartData;
  
  switch (vizType.toUpperCase()) {
    case 'BADGE':
      // Badge는 series 배열에 단일 값을 가져야 함
      if (!data.series || !Array.isArray(data.series) || data.series.length === 0) {
        return false;
      }
      const series = data.series[0];
      if (!series || (!series.values && series.values !== 0)) {
        return false;
      }
      return true;
      
    case 'GAUGE':
      // Gauge도 Badge와 유사한 구조
      return validateChartSeries(chartData, 'BADGE');
      
    case 'TABLE':
      // Table은 columns와 rows가 필요
      if (!data.columns || !data.rows) {
        return false;
      }
      return true;
      
    case 'LINE':
    case 'BAR':
      // Line/Bar는 categories와 series가 필요
      if (!data.series || !Array.isArray(data.series)) {
        return false;
      }
      return true;
      
    default:
      // 기본적으로 통과
      return true;
  }
};

/**
 * SQL 파라미터 값 타입 추론
 * @param {*} value - 파라미터 값
 * @returns {string} 추론된 타입 ('number', 'string', 'boolean', 'null', 'unknown')
 */
export const inferParameterType = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  
  const type = typeof value;
  if (type === 'number' || type === 'string' || type === 'boolean') {
    return type;
  }
  
  // 날짜 객체 확인
  if (value instanceof Date) {
    return 'date';
  }
  
  // 배열 확인
  if (Array.isArray(value)) {
    return 'array';
  }
  
  // 객체 확인
  if (type === 'object') {
    return 'object';
  }
  
  return 'unknown';
};

/**
 * 디버그용 차트 데이터 로깅
 * @param {string} componentId - 컴포넌트 ID
 * @param {Object} chartData - 차트 데이터
 * @param {Object} params - SQL 파라미터
 */
export const debugChartData = (componentId, chartData, params = {}) => {
  // Debug function disabled to reduce console output
  return;
};

export default {
  validateBadgeValue,
  validateChartSeries,
  inferParameterType,
  debugChartData
};